import React from 'react';
import {
  Alert, Badge, Button, ConfirmDialog, Drawer, DrawerBody, DrawerHeader, EmptyState,
  SkeletonRow, TextInput, Textarea, useToast,
} from '@parsina/framework';
import { SearchX } from 'lucide-react';
import { DEFAULT_CURRENCY } from '@parsina/framework/ui/lib/currency';

import { useTranslation, LTR_TEXT, toAsciiDigits } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { useShortcut } from '../../lib/shortcuts';
import { EMPTY } from '../../lib/format';

/**
 * The pieces every marketplace view shares: the drawer, the enum labels, the
 * table skeleton, and — the one with rules in it — `ActionConfirm`, the
 * confirm step every review, close and mark-paid goes through.
 */

/** Rows per page requested from the BFF (cursor paging, «نمایش بیشتر»). */
export const PAGE_LIMIT = 50;
/** Mirrors the BFF's `NOTE_MIN`: a rejection shorter than this is no reason. */
export const NOTE_MIN = 3;
export const NOTE_MAX = 500;

/** A catalogue label for a service vocabulary value; an unknown value is echoed. */
export function useEnumLabel(prefix) {
  const { t, has } = useTranslation();
  return React.useCallback((value) => {
    if (value == null || value === '') return EMPTY;
    const key = `${prefix}.${value}`;
    return has && has(key) ? t(key) : String(value);
  }, [t, has, prefix]);
}

/** A machine identifier (slug, reference, account): LTR island, monospace. */
export function Code({ children, className = '' }) {
  if (children == null || children === '') return <span>{EMPTY}</span>;
  return <span {...LTR_TEXT} className={`font-mono text-[12px] ${className}`}>{children}</span>;
}

/** One labelled fact in a drawer's definition list. */
export function Fact({ label, value, wide }) {
  return (
    <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words font-semibold text-foreground">{value == null || value === '' ? EMPTY : value}</dd>
    </div>
  );
}

const TONE = {
  pending: 'warning', approved: 'success', rejected: 'destructive', suspended: 'destructive',
  not_requested: 'outline', open: 'outline', closed: 'warning', paid: 'success',
};

export function StatusChip({ value, prefix, 'data-testid': testId }) {
  const label = useEnumLabel(prefix);
  if (!value) return <span>{EMPTY}</span>;
  return <Badge variant={TONE[value] || 'outline'} data-testid={testId} data-tone={TONE[value] || 'outline'}>{label(value)}</Badge>;
}

/** An IRR row — or one that names no currency, which is IRR. */
export const isIrr = (currency) => !currency || currency === DEFAULT_CURRENCY;

/**
 * Whether every row of a page is IRR. Then the page reads exactly as it always
 * has: headers say «(تومان)» and figures carry no unit. Stores keep their books
 * in different currencies, so a page that mixes them names no unit in its
 * headers and puts one on every figure instead — and nothing is ever summed
 * across its rows (docs/clinic-currency/CONTRACTS.md §0 rule 4).
 */
export const allIrr = (rows) => (rows || []).every((r) => isIrr(r?.currency));

/**
 * A money cell: minor units of `currency` in, the figure a human reads out,
 * through the one seam.
 *
 * IRR (or no currency) is whole rials → toman with no unit, exactly as before:
 * the column header names it. Any other currency always carries its ISO code
 * («۱٬۲۵۰٫۵۰ AED»), so it can never be read as toman under any header.
 * `withUnit` also puts this app's toman word on an IRR figure, for a table whose
 * rows mix currencies and whose header therefore names none.
 */
export function MoneyCell({ rials, currency, withUnit = false, className = '' }) {
  const { t, formatMoney } = useTranslation();
  const text = formatMoney(rials, {
    currency,
    withUnit: withUnit || !isIrr(currency),
    unitLabel: t('common.units.toman'),
    unitStyle: 'short',
  });
  return <span className={`whitespace-nowrap tabular-nums ${className}`}>{text}</span>;
}

/** A table's loading body in the real frame. */
export function TableSkeleton({ cols, label, 'data-testid': testId }) {
  return (
    <div data-testid={testId} data-state="loading" role="status" aria-busy="true" aria-label={label}>
      <table className="w-full">
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The kit Drawer with a named dialog, Esc through the registry, and a test hook. */
export function MarketDrawer({ onClose, title, sub, children, 'data-testid': testId }) {
  const { t } = useTranslation();
  useShortcut('esc', onClose, {
    label: t('shortcuts.action.closeDrawer'),
    group: t('shortcuts.groups.marketplace'),
  });
  return (
    <Drawer open onClose={onClose} width={560}>
      <div role="dialog" aria-modal="true" aria-label={title} className="flex h-full min-h-0 flex-col" data-testid={testId}>
        <DrawerHeader title={title} sub={sub} onClose={onClose} />
        <DrawerBody>{children}</DrawerBody>
      </div>
    </Drawer>
  );
}

/** The drawer body when the row the URL names is not in the loaded page. */
export function NotInList({ 'data-testid': testId }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
      title={t('marketplace.drawer.notInList')}
      description={t('marketplace.drawer.notInListDesc')}
      data-testid={testId}
    />
  );
}

/**
 * The confirm step of every marketplace write.
 *
 * `field` decides the input: `'note'` (optional), `'rejectNote'` (required,
 * ≥ NOTE_MIN, validated inline) or `'reference'` (required, LTR). The confirm
 * is disabled until the input is valid, so the BFF's own refusal is a second
 * line, not the first.
 *
 * Lifecycle (DESIGN_CONCEPT §2.4): pending disables both buttons; success
 * closes, toasts and hands the updated row to `onDone`; failure keeps the
 * dialog open with every keystroke and reports inline, and the same message is
 * mirrored onto the drawer via `onError` so it survives a cancel.
 */
export function ActionConfirm({
  action, onClose, onDone, onError, testId,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [value, setValue] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setValue(''); setTouched(false); setError(null); setPending(false);
  }, [action?.key]);

  if (!action) return null;

  const trimmed = value.trim();
  const invalid = action.field === 'rejectNote'
    ? trimmed.length < NOTE_MIN
    : action.field === 'reference' ? !trimmed : false;
  const inlineError = touched && invalid
    ? (action.field === 'reference'
      ? t('marketplace.settlements.confirm.referenceRequired')
      : t('marketplace.note.required', { min: NOTE_MIN }))
    : undefined;

  const run = async () => {
    setTouched(true);
    if (invalid || pending) return;
    setPending(true);
    setError(null);
    try {
      const data = await gql(action.document, action.variables(action.field === 'reference' ? toAsciiDigits(trimmed) : (trimmed || null)));
      toast.success(action.success);
      onDone?.(data);
      onClose();
    } catch (e) {
      const message = errorMessage(e, t);
      setError(message);
      onError?.(message);
    } finally {
      setPending(false);
    }
  };

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => { if (!open && !pending) onClose(); }}
      title={action.title}
      description={action.body}
      confirmLabel={action.confirmLabel}
      cancelLabel={t('common.actions.cancel')}
      destructive={action.destructive}
      loading={pending}
      confirmDisabled={invalid}
      onConfirm={run}
      data-testid={testId}
      confirmTestId={`${testId}-confirm`}
      cancelTestId={`${testId}-cancel`}
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert variant="error" data-testid={`${testId}-error`}>{error}</Alert> : null}
        {action.field === 'reference' ? (
          <div data-testid={`${testId}-reference`}>
            <TextInput
              label={t('marketplace.settlements.confirm.reference')}
              required
              value={value}
              onChange={(e) => setValue(e.target?.value ?? e)}
              onBlur={() => setTouched(true)}
              dir="ltr"
              maxLength={120}
              error={inlineError}
              disabled={pending}
            />
          </div>
        ) : action.field ? (
          <div data-testid={`${testId}-note`}>
            <Textarea
              label={action.field === 'rejectNote' ? t('marketplace.note.rejectLabel') : t('marketplace.note.optional')}
              required={action.field === 'rejectNote'}
              helperText={action.field === 'rejectNote' ? t('marketplace.note.rejectHelp') : undefined}
              value={value}
              onChange={(e) => setValue(e.target?.value ?? e)}
              onBlur={() => setTouched(true)}
              error={inlineError}
              maxLength={NOTE_MAX}
              showCount
              rows={3}
              disabled={pending}
            />
          </div>
        ) : null}
      </div>
    </ConfirmDialog>
  );
}

/** «نمایش بیشتر» under a cursor-paged table. */
export function LoadMore({ onClick, pending, 'data-testid': testId }) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center border-t border-border p-2">
      <Button variant="ghost" size="sm" onClick={onClick} disabled={pending} data-testid={testId}>
        {t('marketplace.loadMore')}
      </Button>
    </div>
  );
}

/**
 * Cursor-paged list state for one view. `variables` changes → reload from the
 * first page; `more()` appends. A refetch after a write dims (never blanks).
 */
export function usePagedList(document, pick, variables) {
  const { t } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [moreLoading, setMoreLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const key = JSON.stringify(variables);
  const first = React.useRef(true);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    return gql(document, { ...JSON.parse(key), limit: PAGE_LIMIT })
      .then((d) => { const page = pick(d); setRows(page?.items || []); setCursor(page?.nextCursor || null); })
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, key, t]);

  React.useEffect(() => {
    load({ quiet: !first.current && rows.length > 0 });
    first.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const more = () => {
    if (!cursor) return;
    setMoreLoading(true);
    gql(document, { ...JSON.parse(key), cursor, limit: PAGE_LIMIT })
      .then((d) => { const page = pick(d); setRows((r) => [...r, ...(page?.items || [])]); setCursor(page?.nextCursor || null); })
      .catch((e) => toast.error(errorMessage(e, t)))
      .finally(() => setMoreLoading(false));
  };

  /** Replace one row in place after a write returned it. */
  const replace = (match, next) => setRows((r) => r.map((row) => (match(row) ? { ...row, ...next } : row)));

  return {
    rows, cursor, loading, refreshing, moreLoading, error, reload: () => load({ quiet: true }), retry: () => load(), more, replace,
  };
}
